import { memo, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, FormControlLabel, InputLabel, Typography } from '@mui/material';
import styles from './Esign.module.scss';
import classNames from 'classnames/bind';
import SendDocumentDialog from './send-document/SendDocumentDialog';
import { handleGeneratePDF, handleRevokeDocument } from '~/thunks/estimate/estimateThunk';
import { useAppDispatch } from '~/redux/hooks';
import { FileIcon } from '~/assets/icons';
import { catchError, notify } from '~/utils/helper';
import { IResponseGetFile } from '~/utils/interface/file';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';

const cx = classNames.bind(styles);

type FileItemProps = {
  editData?: any;
  mode?: string;
  form?: any;
  customerEmail?: string;
};
function Esign(props: FileItemProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/job');
  const [openSenDocumentDialog, setOpenSendDocumentDialog] = useState<boolean>(false);
  const { editData, mode, form } = props;
  const [documentFile, setDocumentFile] = useState<IResponseGetFile>();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [openAlertRevoke, setOpenAlerRevoke] = useState<boolean>(false);
  const [signDocument, setSignDocument] = useState<boolean>(false);
  const [buttonGeneratePDF, setButtonGeneratePDF] = useState<boolean>(false);
  const [buttonRevoke, setButtonRevoke] = useState<boolean>(false);
  const [buttonSign, setButtonSign] = useState<boolean>(false);

  useEffect(() => {
    setDocumentFile(editData.documentFile);
  }, [editData.documentFile]);

  useEffect(() => {
    editData.signDocumentId && setSignDocument(true);
  }, [editData.signDocumentId]);

  useEffect(() => {
    if (!editData?.documentFile || editData?.documentFile?.signStatus === null) setButtonGeneratePDF(true);
    else setButtonGeneratePDF(false);

    if (editData?.documentFile?.signStatus !== 'Signed' && !signDocument) setButtonSign(true);
    else setButtonSign(false);

    if (editData?.documentFile?.signStatus !== 'Signed' && signDocument) setButtonRevoke(true);
    else setButtonRevoke(false);
  }, [editData, signDocument]);

  useEffect(() => {
    form.setFieldValue('isGeneratePDF', isChecked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChecked]);

  useEffect(() => {
    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('isGeneratePDFPricing', true);
    } else {
      form.setFieldValue('isGeneratePDFPricing', editData.isGeneratePDFPricing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) setIsChecked(true);
    else setIsChecked(false);
  };

  const handleChangePricing = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    form.setFieldValue('isGeneratePDFPricing', checked);
  };

  const hanleGeneratePDF = async (estimateId: number) => {
    const result = await dispatch(handleGeneratePDF(props.editData.id)).unwrap();
    result && setDocumentFile(result);
    notify('success', t('createSuccess', { ns: 'admin/common' }));
  };

  const revokeDocument = async () => {
    if (!documentFile?.id) return;
    try {
      await dispatch(handleRevokeDocument(documentFile.id)).unwrap();
      setSignDocument(false);
      setButtonGeneratePDF(true);
      setButtonRevoke(false);
      setButtonSign(true);
      setOpenAlerRevoke(false);
      notify('success', t('revokeSuccess', { ns: 'admin/common' }));
    } catch (e) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  };

  return (
    <Box className={cx('file-container')}>
      <Box className='bg-[#F3F9FF] w-full my-4 p-5 font-[600] text-[14px] border-y-2 border-solid border-y-[#e5eaef]'>
        {t('estimate.esign.title')}
      </Box>
      <Box className={cx('box-file-container')}>
        {mode === DIALOG_MODE_CREATE && (
          <Box className='pl-5 flex'>
            <FormControlLabel
              control={
                <Checkbox
                  className='w-[20px] h-[20px]'
                  color='primary'
                  onChange={(event) => handleChange(event)}
                  checked={isChecked}
                />
              }
              label={t('estimate.esign.generatePDF')}
              value='true'
            />
          </Box>
        )}
        <Box className='pl-5 flex mb-[10px] mt-[10px]'>
          <FormControlLabel
            control={
              <Checkbox
                className='w-[20px] h-[20px]'
                color='primary'
                onChange={handleChangePricing}
                checked={form.values.isGeneratePDFPricing}
              />
            }
            label={t('estimate.esign.titlePricing')}
            value='true'
          />
        </Box>
        {mode === DIALOG_MODE_EDIT && (
          <Box className='pl-5'>
            {buttonGeneratePDF && (
              <Button color='primary' variant='outlined' className='mr-3' onClick={() => hanleGeneratePDF(editData.id)}>
                {t('estimate.esign.generatePDF')}
              </Button>
            )}
            {buttonSign && (
              <Button color='primary' variant='contained' onClick={() => setOpenSendDocumentDialog(true)}>
                {t('estimate.esign.sendDocument')}
              </Button>
            )}
            {buttonRevoke && (
              <Button color='primary' variant='contained' className='mr-3' onClick={() => setOpenAlerRevoke(true)}>
                {t('estimate.esign.revokeDocument')}
              </Button>
            )}
            {documentFile && (
              <Box className='border-2 border-solid my-2 relative'>
                <Box className='flex justify-flex-start w-full'>
                  <Box onClick={() => window.open(documentFile.url)} className='w-[100px] h-[100px]'>
                    <FileIcon className='mx-auto my-[15px] w-[70px] h-[70px]' />
                  </Box>
                  <Box className='block border-l-2 px-[50px] flex-1'>
                    <Box className='flex mt-3 w-full'>
                      <InputLabel className='w-[100px] content-center '> {t('estimate.fileItem.name')}</InputLabel>
                      <Typography>{documentFile.name}</Typography>
                    </Box>
                    <Box className='flex mt-3 w-full'>
                      <InputLabel className='w-[100px] content-center'> {t('estimate.fileItem.note')}</InputLabel>
                      <Typography>{documentFile.note}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <SendDocumentDialog
        open={openSenDocumentDialog}
        id={props.editData.id}
        documentFile={documentFile}
        setOpen={(value, success) => {
          setOpenSendDocumentDialog(value);
          if (success) {
            setButtonGeneratePDF(false);
            setButtonSign(false);
            setButtonRevoke(true);
          }
        }}
        customerEmail={props.customerEmail}
      />
      <ModalAlertDelete
        open={openAlertRevoke}
        description={t('estimate.esign.alertRevoke')}
        onCancel={() => setOpenAlerRevoke(false)}
        onConfirm={revokeDocument}
      />
    </Box>
  );
}

export default memo(Esign);
